import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Scroll-Funktion für die Startseite
  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    
    // Wenn wir nicht auf der Startseite sind, navigiere zur Startseite mit Anker
    if (location.pathname !== '/') {
      window.location.href = `/#${id}`;
      return;
    }
    
    // Mobile Menu schließen
    setIsMenuOpen(false);
    
    // Einfache, zuverlässige Scroll-Lösung
    setTimeout(() => {
      const element = document.getElementById(id);
      if (element) {
        const headerHeight = 64; // Header Höhe
        const extraSpace = id === 'kontakt' ? 100 : 80; // Mehr Platz für Kontaktformular
        const targetPosition = element.offsetTop + (id === 'kontakt' ? -extraSpace : extraSpace) - headerHeight;
        
        window.scrollTo({
          top: id === 'kontakt' ? Math.max(0, targetPosition) : targetPosition,
          behavior: 'smooth'
        });
        
        // Spezielle Behandlung für Kontaktformular: Fokussiere das erste Eingabefeld
        if (id === 'kontakt') {
          setTimeout(() => {
            const firstNameInput = document.getElementById('name');
            if (firstNameInput) {
              firstNameInput.focus();
              
              // Für mobile Geräte: Zusätzliche Behandlung für bessere UX
              if (window.innerWidth <= 768) {
                setTimeout(() => {
                  firstNameInput.focus();
                  // Verhindere Zoom auf iOS bei Input-Focus
                  firstNameInput.style.fontSize = '16px';
                }, 300);
              }
            }
          }, 800); // Warte bis Scroll-Animation fertig ist
        }
      }
    }, 150);
  };

  // Scroll-to-top Funktion für das Logo
  const scrollToTop = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    
    // Wenn wir nicht auf der Startseite sind, navigiere zur Startseite
    if (location.pathname !== '/') {
      window.location.href = '/';
      return;
    }
    
    // Wenn wir auf der Startseite sind, scrolle nach oben
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <header className="bg-white shadow-md sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo and Club Name - jetzt als Link zur Startseite */}
          <div className="flex items-center">
            <NavLink to="/" className="flex-shrink-0 flex items-center hover:opacity-80 transition-opacity" onClick={scrollToTop}>
              <img 
                src="/images/logo.png" 
                alt="Flugsportverein Otto Lilienthal Bad Langensalza" 
                className="h-12 w-auto"
              />
              <div className="ml-3 flex flex-col">
                <h1 className="text-sm font-bold text-gray-900 leading-tight">
                  Flugsportverein
                </h1>
                <h2 className="text-sm font-semibold text-gray-900 leading-tight">
                  Otto Lilienthal
                </h2>
              </div>
            </NavLink>
          </div>

          {/* Desktop Navigation mit weniger Abstand zwischen Menüpunkten */}
          <div className="hidden md:flex md:items-center">
            <nav className="flex space-x-3 items-center mr-4">
              <a href="#rundflug" onClick={(e) => scrollToSection(e, 'rundflug')} className="text-gray-900 hover:text-blue-600 px-2.5 py-2 text-sm font-medium whitespace-nowrap transition-colors relative after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-blue-600 after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:origin-left">
                Rundflüge
              </a>
              <a href="#ueber-uns" onClick={(e) => scrollToSection(e, 'ueber-uns')} className="text-gray-900 hover:text-blue-600 px-2.5 py-2 text-sm font-medium whitespace-nowrap transition-colors relative after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-blue-600 after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:origin-left">
                Über uns
              </a>
              <a href="#impressionen" onClick={(e) => scrollToSection(e, 'impressionen')} className="text-gray-900 hover:text-blue-600 px-2.5 py-2 text-sm font-medium whitespace-nowrap transition-colors relative after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-blue-600 after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:origin-left">
                Impressionen
              </a>
              <a href="#pilot-werden" onClick={(e) => scrollToSection(e, 'pilot-werden')} className="text-gray-900 hover:text-blue-600 px-2.5 py-2 text-sm font-medium whitespace-nowrap transition-colors relative after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-blue-600 after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:origin-left">
                Pilot werden
              </a>
              <a href="#ausbildung" onClick={(e) => scrollToSection(e, 'ausbildung')} className="text-gray-900 hover:text-blue-600 px-2.5 py-2 text-sm font-medium whitespace-nowrap transition-colors relative after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-blue-600 after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:origin-left">
                Ausbildung
              </a>
              <a href="#flugzeuge" onClick={(e) => scrollToSection(e, 'flugzeuge')} className="text-gray-900 hover:text-blue-600 px-2.5 py-2 text-sm font-medium whitespace-nowrap transition-colors relative after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-blue-600 after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:origin-left">
                Flugzeuge
              </a>
              <a href="#flugplatz" onClick={(e) => scrollToSection(e, 'flugplatz')} className="text-gray-900 hover:text-blue-600 px-2.5 py-2 text-sm font-medium whitespace-nowrap transition-colors relative after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-blue-600 after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:origin-left">
                Flugplatz
              </a>
              <a href="#veranstaltungen" onClick={(e) => scrollToSection(e, 'veranstaltungen')} className="text-gray-900 hover:text-blue-600 px-2.5 py-2 text-sm font-medium whitespace-nowrap transition-colors relative after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-blue-600 after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:origin-left">
                Veranstaltungen
              </a>
              <a href="#kontakt" onClick={(e) => scrollToSection(e, 'kontakt')} className="text-gray-900 hover:text-blue-600 px-2.5 py-2 text-sm font-medium whitespace-nowrap transition-colors relative after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-blue-600 after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:origin-left">
                Kontakt
              </a>
            </nav>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={toggleMenu}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
              aria-expanded="false"
            >
              <span className="sr-only">Menü öffnen</span>
              {/* Icon when menu is closed */}
              <svg
                className={`${isMenuOpen ? 'hidden' : 'block'} h-6 w-6`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
              {/* Icon when menu is open */}
              <svg
                className={`${isMenuOpen ? 'block' : 'hidden'} h-6 w-6`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu, toggle classes based on menu state */}
      <div className={`${isMenuOpen ? 'block' : 'hidden'} md:hidden`}>
        <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
          <a href="#rundflug" onClick={(e) => scrollToSection(e, 'rundflug')} className="block px-3 py-2 rounded-md text-base font-medium text-gray-900 hover:text-primary hover:bg-gray-50">
            Rundflüge
          </a>
          <a href="#ueber-uns" onClick={(e) => scrollToSection(e, 'ueber-uns')} className="block px-3 py-2 rounded-md text-base font-medium text-gray-900 hover:text-primary hover:bg-gray-50">
            Über uns
          </a>
          <a href="#impressionen" onClick={(e) => scrollToSection(e, 'impressionen')} className="block px-3 py-2 rounded-md text-base font-medium text-gray-900 hover:text-primary hover:bg-gray-50">
            Impressionen
          </a>
          <a href="#pilot-werden" onClick={(e) => scrollToSection(e, 'pilot-werden')} className="block px-3 py-2 rounded-md text-base font-medium text-gray-900 hover:text-primary hover:bg-gray-50">
            Pilot werden
          </a>
          <a href="#ausbildung" onClick={(e) => scrollToSection(e, 'ausbildung')} className="block px-3 py-2 rounded-md text-base font-medium text-gray-900 hover:text-primary hover:bg-gray-50">
            Ausbildung
          </a>
          <a href="#flugzeuge" onClick={(e) => scrollToSection(e, 'flugzeuge')} className="block px-3 py-2 rounded-md text-base font-medium text-gray-900 hover:text-primary hover:bg-gray-50">
            Flugzeuge
          </a>
          <a href="#flugplatz" onClick={(e) => scrollToSection(e, 'flugplatz')} className="block px-3 py-2 rounded-md text-base font-medium text-gray-900 hover:text-primary hover:bg-gray-50">
            Flugplatz
          </a>
          <a href="#veranstaltungen" onClick={(e) => scrollToSection(e, 'veranstaltungen')} className="block px-3 py-2 rounded-md text-base font-medium text-gray-900 hover:text-primary hover:bg-gray-50">
            Veranstaltungen
          </a>
          <a href="#kontakt" onClick={(e) => scrollToSection(e, 'kontakt')} className="block px-3 py-2 rounded-md text-base font-medium text-gray-900 hover:text-primary hover:bg-gray-50">
            Kontakt
          </a>
        </div>
      </div>
    </header>
  );
};

export default Header; 